import { OAuthCredential, signInWithCredential } from "firebase/auth";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getRegion } from "features/regions/selectors";
import useSignUpJourneyStateOnMount from "features/sign-up/global-sign-up-modal/hooks/useSignUpJourneyStateOnMount";
import {
  makeInternalLink,
  pathEndings
} from "features/sign-up/global-sign-up-modal/utils";
import { useRedirectOnSignInCallback } from "features/sign-up/hooks";
import useOnSignUpSuccess from "features/sign-up/hooks/useOnSignUpSuccess";
import {
  proceedUserSignIn,
  proceedUserSignUp
} from "features/user/actions/auth/sso/utils";

import { getFirebaseAuth } from "../auth";
import { ProviderSSO } from "../auth/types";

export default function useAuthUser() {
  const dispatch = useDispatch();
  const auth = getFirebaseAuth();
  const region = useSelector(getRegion)!;

  const { redirectAuthenticatedUserTo } = useSignUpJourneyStateOnMount();
  const onSignInSuccess = useRedirectOnSignInCallback(
    redirectAuthenticatedUserTo
  );

  const onSignUpSuccess = useOnSignUpSuccess({
    linkToNextPage: makeInternalLink(pathEndings.userDetails),
    isSSO: true
  });

  return useCallback(
    async ({
      credentials,
      provider,
      isNewUser
    }: {
      credentials: string;
      provider: ProviderSSO;
      isNewUser: boolean;
    }) => {
      const credentialsParsed = OAuthCredential.fromJSON(credentials);

      if (credentialsParsed) {
        const response = await signInWithCredential(auth, credentialsParsed);

        if (isNewUser) {
          await dispatch(
            proceedUserSignUp({
              region,
              response,
              providerName: provider,
              onSignUpSuccess
            })
          );
        } else {
          await dispatch(
            proceedUserSignIn({
              region,
              providerName: provider,
              onSignInSuccess
            })
          );
        }
      }
    },
    [dispatch, onSignInSuccess, onSignUpSuccess, region, auth]
  );
}
